import { CheckIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Flex,
  Grid,
  Heading,
  HStack,
  useClipboard,
  Text,
  Image,
  Stack,
  SkeletonCircle,
  Skeleton,
  VStack
} from "@chakra-ui/react";
import { shortenAddress } from "@usedapp/core";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppContent } from "../../components/AppContent";
import { NftItem } from "../../components/NftItem";
import { images } from "../../core/constants/images";
import { ICollection } from "../../core/models/collection";
import { INft } from "../../core/models/nft";
import { IUser } from "../../core/models/user";
import { collectionService } from "../../services/collection.service";
import { nftService } from "../../services/nft.service";
import { userService } from "../../services/user.service";
import { Helmet } from "react-helmet";
import { SharedButton } from "../../components/SharedButton";
import { useTranslation } from "react-i18next";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import ExpandableText from "../../components/ExpandableText";

const CollectionDetail = () => {
  const params = useParams();
  const { t } = useTranslation([I18N_NAMESPACE.COLLECTION_DETAIL]);
  const navigate = useNavigate();
  const { hasCopied: hasCopiedAddress, onCopy: onCopyAddress } = useClipboard(params?.id || "");
  const [collection, setCollection] = useState<ICollection>();
  const [nfts, setNfts] = useState<INft[]>();
  const [owner, setOwner] = useState<IUser>();

  const [isFetchingNfts, setIsFetchingNfts] = useState(false);
  const [isFetchingCollectionInfo, setIsFetchingCollectionInfo] = useState(false);

  useEffect(() => {
    async function getDetail() {
      if (!params.id) return;
      setIsFetchingCollectionInfo(true);
      const result = await collectionService.getDetail(params.id);

      if (result.data.owner_of) {
        const ownerResult = await userService.getProfileById(result.data.owner_of);
        setOwner(ownerResult.data);
      }

      setCollection(result.data);
      setIsFetchingCollectionInfo(false);
    }

    async function getNfts() {
      if (!params.id) return;
      setIsFetchingNfts(true);
      const result = await nftService.getNtfsByCollection(params.id);
      setNfts(result?.data || []);
      setIsFetchingNfts(false);
    }

    getDetail();
    getNfts();
  }, []);

  const onClickOwner = () => {
    navigate(`/profile/assets/${owner?.id}`);
  };

  return (
    <VStack>
      <Box height={{ base: "120px", md: "240px" }} backgroundColor="gray.200" width="100%">
        <Skeleton isLoaded={!isFetchingCollectionInfo} height={"full"} width={"full"}>
          <Image
            src={collection?.banner}
            fallbackSrc={images.collectionCoverDefault}
            objectFit={"cover"}
            width="100%"
            height="100%"
          />
        </Skeleton>
      </Box>
      <AppContent>
        <Grid gridTemplateColumns={{ base: "1fr", md: "1fr 2.3fr" }} gridGap={6} paddingBottom={5}>
          <Box position={"relative"}>
            <Flex flexDirection="column" alignItems="flex-start" padding="0px 20px">
              <Box
                height={"140px"}
                width={"140px"}
                position="absolute"
                top="-70px"
                background={"#fff"}
                padding={2}
                borderRadius={"full"}
              >
                <SkeletonCircle isLoaded={!isFetchingCollectionInfo} height={"full"} width={"full"}>
                  <Image
                    height="100%"
                    width="100%"
                    // size="2xl"
                    src={collection?.logo || ""}
                    borderRadius={"full"}
                    fallbackSrc={images.collectionLogoDefault}
                  />
                </SkeletonCircle>
              </Box>

              <Skeleton isLoaded={!isFetchingCollectionInfo} marginTop={"20"} minHeight={"40px"} w={"100%"}>
                <Flex pt={0} width={"100%"} justifyContent={"space-between"}>
                  <Heading size="lg">{collection?.name}</Heading>
                  <SharedButton content={`Checkout ${collection?.name}'s profile on @bePAY`} />
                </Flex>
              </Skeleton>

              <Skeleton isLoaded={!isFetchingCollectionInfo} marginTop={"12px"}>
                <Flex alignItems={"center"}>
                  <HStack
                    fontWeight="700"
                    color={"rgb(128, 128, 128)"}
                    fontSize={"14px"}
                    background="rgba(4, 4, 5, 0.04)"
                    padding="4px 12px"
                    borderRadius="full"
                    onClick={onCopyAddress}
                    cursor={"pointer"}
                    alignItems={"center"}
                    transition={"all 0.12s ease-in-out 0s"}
                    _hover={{
                      background: "rgba(4, 4, 5, 0.08)",
                    }}
                  >
                    {hasCopiedAddress ? (
                      <>
                        <Text>{t("Copied")}</Text>
                        <CheckIcon />
                      </>
                    ) : (
                      <>
                        <Image src={images.ethHighlight} height={4} width={4} />
                        <Text>{params.id && shortenAddress(params.id)}</Text>
                      </>
                    )}
                  </HStack>
                  {!!owner && (
                    <>
                      <Text padding={"0px 8px"} fontSize={"small"} color={"gray.400"}>
                        {t("by")}
                      </Text>
                      <HStack onClick={onClickOwner} cursor={"pointer"} _hover={{ color: "#000" }}>
                        <Avatar size={"sm"} src={owner?.image || ""} />
                        <Text fontWeight={700}>{owner.display_name || (owner.id && shortenAddress(owner.id))}</Text>
                      </HStack>
                    </>
                  )}
                </Flex>
              </Skeleton>
              
              <Skeleton isLoaded={!isFetchingCollectionInfo} marginTop={"20px"} width={"full"}>
                <Flex borderWidth={1} gap={4} borderRadius={"xl"} width={"100%"}>
                  <Stack alignItems={"center"} padding={3}>
                    <Text fontSize={"small"} color={"gray.500"}>
                      {t("Collection of")}
                    </Text>
                    <Text fontWeight={700}>{nfts?.length || "—"}</Text>
                  </Stack>
                  <Stack alignItems={"center"} padding={3}>
                    <Text fontSize={"small"} color={"gray.500"}>
                      {t("Highest price")}
                    </Text>
                    <Text fontWeight={700}>—</Text>
                  </Stack>
                  <Stack alignItems={"center"} padding={3}>
                    <Text fontSize={"small"} color={"gray.500"}>
                      {t("Total Volume")}
                    </Text>
                    <Text fontWeight={700}>—</Text>
                  </Stack>
                </Flex>
              </Skeleton>

              {(isFetchingCollectionInfo || !!collection?.description) && (
                <Skeleton isLoaded={!isFetchingCollectionInfo} marginTop={"20px"} width={"full"}>
                  <ExpandableText textColor={"gray.500"}
                    fontSize="sm"
                    visibleLines={{ base: 3, md: 0 }}
                    collapedButtonText={t("[Read more]")}
                    expandedButtonText={t("[Read less]")}
                  >
                    {collection?.description}
                  </ExpandableText>
                </Skeleton>
              )}
            </Flex>

          </Box>

          <Skeleton isLoaded={!isFetchingCollectionInfo} width={"full"}>
            <Grid
              templateColumns={{
                base: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
                "2xl": "repeat(5, 1fr)",
              }}
              p={4}
              gap={6}>
              {nfts?.map((nft) => (
                <NftItem
                  key={nft.token_id}
                  tokenAddress={nft.token_address}
                  tokenId={nft.token_id}
                  tokenUri={nft.token_uri}
                  bTokenAddress={nft.b_token_address}
                  ownerOf={nft.owner_of}
                  collectionName={nft.token_name}
                  nftName={nft.name}
                  image={nft.image}
                  isListed={nft?.status === "listed"}
                  price={nft.price}
                />
              ))}
            </Grid>
          </Skeleton>
        </Grid>
      </AppContent>
    </VStack>
  );
};

export default React.memo(CollectionDetail);
